import { LocaleConstants } from '@lib-helpers'
import { RegistrationDataModel } from './__generated__/registration'
import { RegistrationActivity } from '@/types/__domain__/registration'

export enum RegistrationFormKeys {
    // General Company Details
    COMPANY_NAME = 'organization',
    COMPANY_ACTIVITIES = 'company-activities',
    HAS_NO_CHAMBER_OF_COMMERCE_NUMBER = 'has-no-chamber-of-commerce-number',
    CHAMBER_OF_COMMERCE_NUMBER = 'chamber-of-commerce-number',
    WEBSITE = 'website',
    PHONE_NUMBER = 'phone-number',
    CHAIN = 'chain',
    CHAIN_FULL_NAME = 'chain-full-name',

    // Main Address
    POSTAL_CODE = 'postal-code',
    STREET = 'address-line1',
    HOUSE_NUMBER = 'address-line2',
    HOUSE_NUMBER_SUFFIX = 'address-line3',
    CITY = 'city',
    COUNTRY_CODE = 'country',
    COUNTRY_FULL_NAME = 'country-full-name',
    REMARKS = 'remarks',
    IS_PICKUP = 'is-pickup',

    // Pickup Address
    PICKUP_POSTAL_CODE = 'pickup-postal-code',
    PICKUP_STREET = 'pickup-address-line1',
    PICKUP_HOUSE_NUMBER = 'pickup-address-line2',
    PICKUP_HOUSE_NUMBER_SUFFIX = 'pickup-address-line3',
    PICKUP_CITY = 'pickup-city',
    PICKUP_COUNTRY_CODE = 'pickup-country',
    PICKUP_COUNTRY_FULL_NAME = 'pickup-country-full-name',
    PICKUP_REMARKS = 'pickup-remarks',

    // Availability
    AVAILABLE_DURING_BREAK = 'available-during-break',
    MONDAY = 'availability-monday',
    MONDAY_1_A = 'availability-monday-1-A',
    MONDAY_1_B = 'availability-monday-1-B',
    MONDAY_2_A = 'availability-monday-2-A',
    MONDAY_2_B = 'availability-monday-2-B',
    TUESDAY = 'availability-tuesday',
    TUESDAY_1_A = 'availability-tuesday-1-A',
    TUESDAY_1_B = 'availability-tuesday-1-B',
    TUESDAY_2_A = 'availability-tuesday-2-A',
    TUESDAY_2_B = 'availability-tuesday-2-B',
    WEDNESDAY = 'availability-wednesday',
    WEDNESDAY_1_A = 'availability-wednesday-1-A',
    WEDNESDAY_1_B = 'availability-wednesday-1-B',
    WEDNESDAY_2_A = 'availability-wednesday-2-A',
    WEDNESDAY_2_B = 'availability-wednesday-2-B',
    THURSDAY = 'availability-thursday',
    THURSDAY_1_A = 'availability-thursday-1-A',
    THURSDAY_1_B = 'availability-thursday-1-B',
    THURSDAY_2_A = 'availability-thursday-2-A',
    THURSDAY_2_B = 'availability-thursday-2-B',
    FRIDAY = 'availability-friday',
    FRIDAY_1_A = 'availability-friday-1-A',
    FRIDAY_1_B = 'availability-friday-1-B',
    FRIDAY_2_A = 'availability-friday-2-A',
    FRIDAY_2_B = 'availability-friday-2-B',
    SATURDAY = 'availability-saturday',
    SATURDAY_1_A = 'availability-saturday-1-A',
    SATURDAY_1_B = 'availability-saturday-1-B',
    SATURDAY_2_A = 'availability-saturday-2-A',
    SATURDAY_2_B = 'availability-saturday-2-B',
    SUNDAY = 'availability-sunday',
    SUNDAY_1_A = 'availability-sunday-1-A',
    SUNDAY_1_B = 'availability-sunday-1-B',
    SUNDAY_2_A = 'availability-sunday-2-A',
    SUNDAY_2_B = 'availability-sunday-2-B',

    // Financial Details
    IBAN = 'iban',
    BIC_SWIFT = 'bic-swift',
    VAT_NUMBER = 'vat-number',
    HAS_NO_VAT_NUMBER = 'has-no-vat-number',
    INVOICE_EMAIL = 'invoice-email',
    INVOICE_LOCALE = 'invoice-locale',
    ACTIVITY_START_DATE = 'activity-start-date',

    // Compliance Details
    COMPLIANCE_COMPANY_CODE = 'compliance-company-code',
    COMPLIANCE_COMPANY_NAME = 'compliance-company-name',

    // Contact Details
    HAS_CONTACT_PERSON = 'has-contact-person',
    FIRST_NAME = 'first-name',
    MIDDLE_NAME = 'middle-name',
    LAST_NAME = 'last-name',
    PRIMARY_PHONE = 'phone',
    SECONDARY_PHONE = 'phone-secondary',
    EMAIL = 'email',
    JOB_TITLE = 'organization-title',

    // External signing
    HAS_EXTERNAL_SIGNER = 'has-external-signer',
    EXTERNAL_SIGNING_FIRST_NAME = 'external-first-name',
    EXTERNAL_SIGNING_MIDDLE_NAME = 'external-middle-name',
    EXTERNAL_SIGNING_LAST_NAME = 'external-last-name',
    EXTERNAL_SIGNING_EMAIL = 'external-email',
    EXTERNAL_SIGNING_JOB_TITLE = 'external-organization-title',

    // Registration Check
    REGISTRATION_CHECK_ANSWERS = 'registration-check',
}

export type ExternalSigningFormKeys = Extract<
    RegistrationFormKeys,
    | RegistrationFormKeys.EXTERNAL_SIGNING_FIRST_NAME
    | RegistrationFormKeys.EXTERNAL_SIGNING_MIDDLE_NAME
    | RegistrationFormKeys.EXTERNAL_SIGNING_LAST_NAME
    | RegistrationFormKeys.EXTERNAL_SIGNING_EMAIL
    | RegistrationFormKeys.EXTERNAL_SIGNING_JOB_TITLE
>

export interface RegistrationContactExternalSigningModel {
    [RegistrationFormKeys.EXTERNAL_SIGNING_FIRST_NAME]: string
    [RegistrationFormKeys.EXTERNAL_SIGNING_MIDDLE_NAME]: string
    [RegistrationFormKeys.EXTERNAL_SIGNING_LAST_NAME]: string
    [RegistrationFormKeys.EXTERNAL_SIGNING_EMAIL]: string
    [RegistrationFormKeys.EXTERNAL_SIGNING_JOB_TITLE]: string
}

export interface RegistrationAvailabilityFormModel {
    [RegistrationFormKeys.AVAILABLE_DURING_BREAK]: boolean
    [RegistrationFormKeys.MONDAY]: boolean
    [RegistrationFormKeys.MONDAY_1_A]: string
    [RegistrationFormKeys.MONDAY_1_B]: string
    [RegistrationFormKeys.MONDAY_2_A]: string
    [RegistrationFormKeys.MONDAY_2_B]: string
    [RegistrationFormKeys.TUESDAY]: boolean
    [RegistrationFormKeys.TUESDAY_1_A]: string
    [RegistrationFormKeys.TUESDAY_1_B]: string
    [RegistrationFormKeys.TUESDAY_2_A]: string
    [RegistrationFormKeys.TUESDAY_2_B]: string
    [RegistrationFormKeys.WEDNESDAY]: boolean
    [RegistrationFormKeys.WEDNESDAY_1_A]: string
    [RegistrationFormKeys.WEDNESDAY_1_B]: string
    [RegistrationFormKeys.WEDNESDAY_2_A]: string
    [RegistrationFormKeys.WEDNESDAY_2_B]: string
    [RegistrationFormKeys.THURSDAY]: boolean
    [RegistrationFormKeys.THURSDAY_1_A]: string
    [RegistrationFormKeys.THURSDAY_1_B]: string
    [RegistrationFormKeys.THURSDAY_2_A]: string
    [RegistrationFormKeys.THURSDAY_2_B]: string
    [RegistrationFormKeys.FRIDAY]: boolean
    [RegistrationFormKeys.FRIDAY_1_A]: string
    [RegistrationFormKeys.FRIDAY_1_B]: string
    [RegistrationFormKeys.FRIDAY_2_A]: string
    [RegistrationFormKeys.FRIDAY_2_B]: string
    [RegistrationFormKeys.SATURDAY]: boolean
    [RegistrationFormKeys.SATURDAY_1_A]: string
    [RegistrationFormKeys.SATURDAY_1_B]: string
    [RegistrationFormKeys.SATURDAY_2_A]: string
    [RegistrationFormKeys.SATURDAY_2_B]: string
    [RegistrationFormKeys.SUNDAY]: boolean
    [RegistrationFormKeys.SUNDAY_1_A]: string
    [RegistrationFormKeys.SUNDAY_1_B]: string
    [RegistrationFormKeys.SUNDAY_2_A]: string
    [RegistrationFormKeys.SUNDAY_2_B]: string
}

export interface RegistrationFormModel
    extends RegistrationAvailabilityFormModel,
        Partial<RegistrationContactExternalSigningModel> {
    [RegistrationFormKeys.COMPANY_NAME]: string
    [RegistrationFormKeys.COMPANY_ACTIVITIES]: RegistrationActivity[]
    [RegistrationFormKeys.HAS_NO_CHAMBER_OF_COMMERCE_NUMBER]: boolean
    [RegistrationFormKeys.CHAMBER_OF_COMMERCE_NUMBER]: string
    [RegistrationFormKeys.WEBSITE]: string
    [RegistrationFormKeys.PHONE_NUMBER]: string
    [RegistrationFormKeys.CHAIN]: number
    [RegistrationFormKeys.CHAIN_FULL_NAME]: string

    [RegistrationFormKeys.POSTAL_CODE]: string
    [RegistrationFormKeys.HOUSE_NUMBER]: string
    [RegistrationFormKeys.HOUSE_NUMBER_SUFFIX]: string
    [RegistrationFormKeys.STREET]: string
    [RegistrationFormKeys.CITY]: string
    [RegistrationFormKeys.COUNTRY_CODE]: string
    [RegistrationFormKeys.COUNTRY_FULL_NAME]: string
    [RegistrationFormKeys.REMARKS]: string
    [RegistrationFormKeys.IS_PICKUP]: boolean

    [RegistrationFormKeys.PICKUP_POSTAL_CODE]: string
    [RegistrationFormKeys.PICKUP_HOUSE_NUMBER]: string
    [RegistrationFormKeys.PICKUP_HOUSE_NUMBER_SUFFIX]: string
    [RegistrationFormKeys.PICKUP_STREET]: string
    [RegistrationFormKeys.PICKUP_CITY]: string
    [RegistrationFormKeys.PICKUP_COUNTRY_CODE]: string
    [RegistrationFormKeys.PICKUP_COUNTRY_FULL_NAME]: string
    [RegistrationFormKeys.PICKUP_REMARKS]: string

    [RegistrationFormKeys.COMPLIANCE_COMPANY_CODE]: string
    [RegistrationFormKeys.COMPLIANCE_COMPANY_NAME]: string

    [RegistrationFormKeys.HAS_CONTACT_PERSON]: boolean
    [RegistrationFormKeys.FIRST_NAME]: string
    [RegistrationFormKeys.MIDDLE_NAME]: string
    [RegistrationFormKeys.LAST_NAME]: string
    [RegistrationFormKeys.PRIMARY_PHONE]: string
    [RegistrationFormKeys.SECONDARY_PHONE]: string
    [RegistrationFormKeys.EMAIL]: string
    [RegistrationFormKeys.JOB_TITLE]: string

    [RegistrationFormKeys.IBAN]: string
    [RegistrationFormKeys.BIC_SWIFT]: string
    [RegistrationFormKeys.VAT_NUMBER]: string
    [RegistrationFormKeys.HAS_NO_VAT_NUMBER]: boolean
    [RegistrationFormKeys.INVOICE_EMAIL]: string
    [RegistrationFormKeys.INVOICE_LOCALE]: LocaleConstants
    [RegistrationFormKeys.ACTIVITY_START_DATE]: Date

    [RegistrationFormKeys.HAS_EXTERNAL_SIGNER]: boolean

    [RegistrationFormKeys.REGISTRATION_CHECK_ANSWERS]: string[][]
}

type SummaryItem = {
    title: string
    label: string
    link?: string
}

export interface SummaryData {
    title: string
    editLinkId: string
    items: SummaryItem[]
    warning?: Warning
}

export interface Warning {
    hasWarning: boolean,
    warningType: WarningType
    commerceNumber?: string
}

export enum WarningType {
    "commerceNumber",
    "address"
}

export interface RegistrationCheckFormModel {
    activityId: number
    answers: string[]
}

export interface RegistrationPostModel extends RegistrationDataModel {}
